import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from '../../config/axios';
import { Display } from './Atoms';

/**
 * QuickPhotoModal — szybkie dodawanie zdjęcia.
 *
 * Port 1:1 ze starego components/modals/QuickPhotoModal.js (488 lin., d3e8373^)
 * z zachowaniem flow:
 *   Krok 1/4 — wybór zdjęcia (aparat z capture="environment" LUB galeria)
 *   Krok 2/4 — tag (12 PHOTO_TAGS)
 *   Krok 3/4 — lokalizacja (poletko + grządka, opcjonalne)
 *   Krok 4/4 — opis (200 znaków max)
 *
 * Zachowane z legacy:
 *   - 5MB limit (nie 10MB)
 *   - walidacja typu: JPG, PNG, GIF, WebP
 *   - auto-advance ze step 1 do step 2 po wczytaniu zdjęcia
 *   - dispatchEvent('photoAdded') po sukcesie (inne komponenty mogą reagować)
 *   - guard isMountedRef przeciw setState po unmount
 *   - reset stanu w handleClose
 *
 * Endpointy:
 *   GET /api/plots
 *   GET /api/plots/:id/beds
 *   POST /api/gallery/quick (multipart: photo, tag?, caption?, bed_id?, plot_id?)
 *
 * Backend whitelist tagów (gallery.js:12):
 *   warzywa, kwiaty, zioła, owoce, siew, zbiór,
 *   podlewanie, problem, pielęgnacja, pogoda, sukces, ogólne
 */

const PHOTO_TAGS = [
  { emoji: '🥕', label: 'Warzywa',     value: 'warzywa' },
  { emoji: '🌸', label: 'Kwiaty',      value: 'kwiaty' },
  { emoji: '🌿', label: 'Zioła',       value: 'zioła' },
  { emoji: '🍎', label: 'Owoce',       value: 'owoce' },
  { emoji: '🌱', label: 'Siew',        value: 'siew' },
  { emoji: '🌾', label: 'Zbiór',       value: 'zbiór' },
  { emoji: '💧', label: 'Podlewanie',  value: 'podlewanie' },
  { emoji: '🐛', label: 'Problem',     value: 'problem' },
  { emoji: '✂️', label: 'Pielęgnacja', value: 'pielęgnacja' },
  { emoji: '🌤️', label: 'Pogoda',     value: 'pogoda' },
  { emoji: '🏆', label: 'Sukces',      value: 'sukces' },
  { emoji: '📸', label: 'Ogólne',      value: 'ogólne' },
];

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024;

export default function QuickPhotoModal({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [plots, setPlots] = useState([]);
  const [selectedPlot, setSelectedPlot] = useState('');
  const [beds, setBeds] = useState([]);
  const [selectedBed, setSelectedBed] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [error, setError] = useState('');

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const loadPlots = useCallback(async () => {
    try {
      const r = await axios.get('/api/plots');
      if (isMountedRef.current) setPlots(r.data || []);
    } catch (e) { /* silent */ }
  }, []);

  const loadBeds = useCallback(async (plotId) => {
    try {
      const r = await axios.get(`/api/plots/${plotId}/beds`);
      if (isMountedRef.current) setBeds((r.data || []).filter(b => !b.actual_harvest_date));
    } catch (e) {
      if (isMountedRef.current) setBeds([]);
    }
  }, []);

  // Reset/init przy otwarciu/zamknięciu
  useEffect(() => {
    if (open) {
      if (plots.length === 0) loadPlots();
    } else {
      setStep(1);
      setPhoto(null);
      setPhotoPreview(null);
      setSelectedTag(null);
      setSelectedPlot('');
      setSelectedBed('');
      setCaption('');
      setError('');
      setUploading(false);
      setProcessingPhoto(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (selectedPlot) loadBeds(selectedPlot);
    else { setBeds([]); setSelectedBed(''); }
  }, [selectedPlot, loadBeds]);

  const handlePhotoSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (f.size > MAX_SIZE) {
      setError('Plik jest za duży. Maksymalny rozmiar: 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(f.type)) {
      setError('Nieprawidłowy typ pliku. Dozwolone: JPG, PNG, GIF, WebP');
      return;
    }

    setError('');
    setProcessingPhoto(true);
    setPhoto(f);

    const reader = new FileReader();
    reader.onload = (ev) => {
      if (!isMountedRef.current) return;
      setPhotoPreview(ev.target.result);
      setProcessingPhoto(false);
      setStep(2); // auto-advance
    };
    reader.onerror = () => {
      if (!isMountedRef.current) return;
      setError('Błąd podczas wczytywania zdjęcia');
      setProcessingPhoto(false);
    };
    reader.readAsDataURL(f);
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!photo) {
      setError('Wybierz zdjęcie');
      return;
    }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('photo', photo);
      if (selectedTag) fd.append('tag', selectedTag);
      if (caption) fd.append('caption', caption);
      if (selectedBed) fd.append('bed_id', selectedBed);
      else if (selectedPlot) fd.append('plot_id', selectedPlot);
      await axios.post('/api/gallery/quick', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Powiadom inne komponenty (Today/Gallery widget) że jest nowe zdjęcie
      window.dispatchEvent(new CustomEvent('photoAdded'));
      onSuccess?.();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Błąd podczas dodawania zdjęcia');
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="qpm-title"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.65)',
        display: 'grid', placeItems: 'center',
        padding: 16,
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      <div className="redesign-root" style={{
        width: '100%', maxWidth: 460,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line-2)',
        borderRadius: 16,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        maxHeight: 'calc(100dvh - 32px)',
        color: 'var(--t-text)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 22px',
          borderBottom: '1px solid var(--t-line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          gap: 14,
        }}>
          <div>
            <div style={{
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.22em', textTransform: 'uppercase',
              color: 'var(--t-sage)', marginBottom: 4,
            }}>Krok {step} z {totalSteps}</div>
            <Display id="qpm-title" size={20} style={{ display: 'block', letterSpacing: '-.02em' }}>
              Szybkie zdjęcie
            </Display>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Zamknij"
            style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(232, 230, 221, 0.06)',
              border: '1px solid var(--t-line-2)',
              color: 'var(--t-text-dim)',
              cursor: 'pointer', fontSize: 16, lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 3, background: 'var(--t-line)' }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: 'var(--t-sage)',
            transition: 'width 250ms ease',
          }} />
        </div>

        {/* Content */}
        <div style={{ padding: 22, overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{
              marginBottom: 14, padding: '10px 12px', borderRadius: 8,
              background: 'rgba(192, 106, 74, 0.10)',
              border: '1px solid rgba(192, 106, 74, 0.25)',
              color: 'var(--t-rust)', fontSize: 12,
            }}>{error}</div>
          )}

          {/* Step 1: Photo */}
          {step === 1 && (
            <div>
              <div style={sectionTitle}>📸 Wybierz zdjęcie</div>

              {processingPhoto && (
                <div style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  height: 160, gap: 12, color: 'var(--t-text-dim)',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    border: '3px solid var(--t-line-2)',
                    borderTopColor: 'var(--t-sage)',
                    animation: 'spin 800ms linear infinite',
                  }} />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  <div style={{ fontSize: 13 }}>Przetwarzanie zdjęcia…</div>
                </div>
              )}

              {photoPreview && !processingPhoto ? (
                <div style={{ position: 'relative' }}>
                  <img
                    src={photoPreview}
                    alt="Podgląd"
                    style={{
                      width: '100%', height: 240, objectFit: 'cover',
                      borderRadius: 12, display: 'block',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                    aria-label="Usuń zdjęcie"
                    style={{
                      position: 'absolute', top: 8, right: 8,
                      width: 32, height: 32, borderRadius: '50%',
                      background: 'var(--t-rust)', color: 'var(--t-bg)',
                      border: 'none', cursor: 'pointer',
                      fontSize: 14, fontWeight: 600,
                    }}
                  >×</button>
                </div>
              ) : !processingPhoto ? (
                <div style={{ display: 'grid', gap: 10 }}>
                  <label style={uploadBox('var(--t-sage)')}>
                    <input
                      type="file"
                      aria-label="Zrób zdjęcie aparatem"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoSelect}
                      style={{ display: 'none' }}
                    />
                    <div style={uploadIcon('var(--t-sage)')}>📷</div>
                    <div style={uploadLabel}>Zrób zdjęcie</div>
                    <div style={uploadSub}>Otwórz aparat urządzenia</div>
                  </label>

                  <label style={uploadBox('var(--t-line-2)')}>
                    <input
                      type="file"
                      aria-label="Wybierz zdjęcie z galerii"
                      accept="image/*"
                      onChange={handlePhotoSelect}
                      style={{ display: 'none' }}
                    />
                    <div style={uploadIcon('var(--t-text-dim)')}>🖼️</div>
                    <div style={uploadLabel}>Wybierz z galerii</div>
                    <div style={uploadSub}>JPG, PNG, GIF, WebP · max 5MB</div>
                  </label>
                </div>
              ) : null}
            </div>
          )}

          {/* Step 2: Tag */}
          {step === 2 && (
            <div>
              <div style={sectionTitle}>🏷️ Wybierz kategorię (opcjonalnie)</div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 10,
              }}>
                {PHOTO_TAGS.map(tag => {
                  const on = selectedTag === tag.value;
                  return (
                    <button
                      key={tag.value}
                      type="button"
                      onClick={() => setSelectedTag(on ? null : tag.value)}
                      style={{
                        padding: '14px 8px', borderRadius: 12,
                        background: on ? 'rgba(168, 196, 158, 0.14)' : 'var(--t-bg)',
                        border: `1px solid ${on ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
                        color: on ? 'var(--t-sage)' : 'var(--t-text-dim)',
                        cursor: 'pointer',
                        transform: on ? 'scale(1.04)' : 'scale(1)',
                        transition: 'all 200ms ease',
                        fontFamily: 'var(--t-sans)',
                      }}
                    >
                      <div style={{ fontSize: 26, marginBottom: 4, lineHeight: 1 }}>{tag.emoji}</div>
                      <div style={{ fontSize: 11, fontWeight: 500 }}>{tag.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Location */}
          {step === 3 && (
            <div>
              <div style={sectionTitle}>📍 Lokalizacja (opcjonalnie)</div>

              <div style={{ marginBottom: 14 }}>
                <div style={fieldLabel}>Działka</div>
                <select
                  aria-label="Działka"
                  value={selectedPlot}
                  onChange={(e) => setSelectedPlot(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Bez lokalizacji</option>
                  {plots.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {selectedPlot && beds.length > 0 && (
                <div>
                  <div style={fieldLabel}>Grządka</div>
                  <select
                    aria-label="Grządka"
                    value={selectedBed}
                    onChange={(e) => setSelectedBed(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">Cała działka</option>
                    {beds.map(b => (
                      <option key={b.id} value={b.id}>
                        Rz. {b.row_number} — {b.plant_name || 'pusta'}
                        {b.plant_variety ? ` (${b.plant_variety})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {plots.length === 0 && (
                <div style={{
                  padding: '12px 14px', borderRadius: 10,
                  background: 'rgba(220, 180, 100, 0.08)',
                  border: '1px solid rgba(220, 180, 100, 0.20)',
                  color: 'var(--t-text-dim)', fontSize: 13, lineHeight: 1.5,
                }}>
                  Nie masz jeszcze żadnej działki. Możesz pominąć ten krok i dodać zdjęcie bez przypisania.
                </div>
              )}
            </div>
          )}

          {/* Step 4: Caption */}
          {step === 4 && (
            <div>
              <div style={sectionTitle}>✍️ Opis (opcjonalnie)</div>
              <textarea
                aria-label="Opis zdjęcia"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={200}
                rows={4}
                placeholder="Co dziś w ogrodzie?"
                style={{
                  ...selectStyle,
                  fontFamily: 'var(--t-sans)',
                  resize: 'vertical',
                  minHeight: 100,
                }}
              />
              <div style={{
                marginTop: 6, textAlign: 'right',
                fontFamily: 'var(--t-mono)', fontSize: 10,
                color: 'var(--t-text-mute)', letterSpacing: '.1em',
              }}>{caption.length} / 200</div>

              {/* Podsumowanie */}
              <div style={{
                marginTop: 18, padding: '14px 16px',
                borderRadius: 12,
                background: 'rgba(232, 230, 221, 0.025)',
                border: '1px solid var(--t-line)',
                fontSize: 12, lineHeight: 1.6,
                color: 'var(--t-text-dim)',
              }}>
                <div style={{
                  fontFamily: 'var(--t-mono)', fontSize: 10,
                  letterSpacing: '.18em', textTransform: 'uppercase',
                  color: 'var(--t-text-mute)', marginBottom: 8,
                }}>Podsumowanie</div>
                <div>📷 Zdjęcie: <span style={{ color: 'var(--t-text)' }}>{photo?.name || '—'}</span></div>
                {selectedTag && (
                  <div>🏷️ Kategoria: <span style={{ color: 'var(--t-text)' }}>
                    {PHOTO_TAGS.find(t => t.value === selectedTag)?.label}
                  </span></div>
                )}
                {selectedPlot && (
                  <div>📍 Lokalizacja: <span style={{ color: 'var(--t-text)' }}>
                    {plots.find(p => String(p.id) === String(selectedPlot))?.name}
                    {selectedBed && beds.find(b => String(b.id) === String(selectedBed)) && (
                      ` · ${beds.find(b => String(b.id) === String(selectedBed)).plant_name || 'rząd'}`
                    )}
                  </span></div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer / nawigacja */}
        <div style={{
          padding: '14px 22px',
          borderTop: '1px solid var(--t-line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 10,
        }}>
          {step > 1 ? (
            <button type="button" onClick={() => setStep(step - 1)} style={cancelBtn}>
              ← Wstecz
            </button>
          ) : (
            <button type="button" onClick={handleClose} style={cancelBtn}>
              Anuluj
            </button>
          )}

          {step < totalSteps ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !photo}
              style={{
                ...primaryBtn,
                opacity: (step === 1 && !photo) ? 0.4 : 1,
                cursor: (step === 1 && !photo) ? 'not-allowed' : 'pointer',
              }}
            >Dalej →</button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={uploading || !photo}
              style={{
                ...primaryBtn,
                opacity: (uploading || !photo) ? 0.4 : 1,
                cursor: (uploading || !photo) ? 'not-allowed' : 'pointer',
              }}
            >{uploading ? 'Wgrywam…' : 'Zapisz w galerii'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

const sectionTitle = {
  fontFamily: 'var(--t-display)', fontSize: 18,
  letterSpacing: '-.01em', color: 'var(--t-text)',
  marginBottom: 16,
};

const fieldLabel = {
  fontFamily: 'var(--t-mono)', fontSize: 10,
  letterSpacing: '.18em', textTransform: 'uppercase',
  color: 'var(--t-text-mute)',
  marginBottom: 6,
};

const selectStyle = {
  width: '100%',
  padding: '12px 14px', borderRadius: 10,
  background: 'var(--t-bg)',
  border: '1px solid var(--t-line-2)',
  color: 'var(--t-text)',
  fontFamily: 'var(--t-sans)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const uploadBox = (borderColor) => ({
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  gap: 8,
  padding: '24px 16px',
  borderRadius: 12,
  background: 'var(--t-bg)',
  border: `2px dashed ${borderColor}`,
  cursor: 'pointer',
  transition: 'background 200ms ease, border-color 200ms ease',
  textAlign: 'center',
});

const uploadIcon = (color) => ({
  width: 56, height: 56, borderRadius: 14,
  background: 'rgba(168, 196, 158, 0.10)',
  display: 'grid', placeItems: 'center',
  fontSize: 30,
  color,
});

const uploadLabel = {
  fontFamily: 'var(--t-display)', fontSize: 16,
  letterSpacing: '-.01em',
  color: 'var(--t-text)',
};

const uploadSub = {
  fontFamily: 'var(--t-sans)', fontSize: 12,
  color: 'var(--t-text-dim)',
};

const primaryBtn = {
  padding: '10px 20px', borderRadius: 10,
  background: 'var(--t-sage)', border: 'none',
  color: 'var(--t-bg)',
  fontFamily: 'var(--t-sans)', fontSize: 14, fontWeight: 500,
  cursor: 'pointer', letterSpacing: '-.01em',
};

const cancelBtn = {
  padding: '10px 16px', borderRadius: 10,
  background: 'transparent',
  border: '1px solid var(--t-line-2)',
  color: 'var(--t-text-dim)',
  fontFamily: 'var(--t-sans)', fontSize: 14,
  cursor: 'pointer',
};
